// useClienteFormXL - Sprint 44 Fase 3 Vista 2.
// Hook del formulario de cliente version XL con:
//   - Carga por :id + new con EMPTY.
//   - Tab controlado (defaulting a 'datos').
//   - Auto-dismiss de msg a 4 segundos.
//   - [FIX bug heredado] set('pais', v) SOLO sobrescribe tasa_impuesto si la
//     tasa actual coincide con el default del pais anterior. Asi, cuando el
//     usuario ya la edito manualmente (valor distinto del default previo), no
//     la pisamos.
//   - Action deactivate/activate desde el form (no solo desde la lista).
//
// Reusa service + utils legacy (contratos backend intactos).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as svc from '../../clientes/services/clientesService';
import { EMPTY, esColombia, ivaDefault } from '../../clientes/form/utils';

const MSG_AUTO_DISMISS_MS = 4000;

export default function useClienteFormXL() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'nuevo';

  const [form, setForm]     = useState(EMPTY);
  const [tab, setTab]       = useState('datos');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [msg, setMsg]       = useState(null);

  const msgTimer = useRef(null);
  const pushMsg = useCallback((kind, text) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    setMsg({ kind, text });
    msgTimer.current = setTimeout(() => setMsg(null), MSG_AUTO_DISMISS_MS);
  }, []);
  useEffect(() => () => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (isNew) {
        if (alive) { setForm(EMPTY); setLoaded(true); }
        return;
      }
      try {
        const d = await svc.getCliente(id);
        if (alive) setForm({ ...EMPTY, ...(d || {}) });
      } catch (e) {
        if (alive) pushMsg('err', 'No se pudo cargar el cliente');
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [id, isNew, pushMsg]);

  // FIX heredado: preservar tasa_impuesto si el usuario la edito manualmente.
  // Regla: solo autoset si la tasa actual es "el default del pais previo"
  // (o 0/vacia). Si el usuario ya la cambio a algo distinto, no tocar.
  const set = useCallback((k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === 'pais') {
        const prevDefault = Number(ivaDefault(f.pais) || 0);
        const nextDefault = Number(ivaDefault(v) || 0);
        const current = Number(f.tasa_impuesto || 0);
        if (current === 0 || current === prevDefault) {
          next.tasa_impuesto = nextDefault;
        }
      }
      return next;
    });
  }, []);

  const buildPayload = useCallback(() => {
    const p = { ...form };
    ['dias_credito', 'limite_credito', 'tasa_impuesto', 'vendedor_id'].forEach((k) => {
      if (p[k] === '' || p[k] === null || p[k] === undefined) p[k] = null;
    });
    if (!esColombia(p.pais)) {
      p.nit = null; p.dv = null;
      p.tipo_documento = null; p.tipo_organizacion = null;
      p.regimen_fiscal = null; p.responsabilidad_fiscal = null;
      p.codigo_dane_municipio = null; p.codigo_dane_departamento = null;
      p.codigo_postal = null;
      p.retencion_fuente = false; p.retencion_iva = false;
      p.retencion_ica = false; p.autorretencion = false;
    }
    return p;
  }, [form]);

  const onSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isNew) {
        const created = await svc.createCliente(payload);
        pushMsg('ok', 'Cliente creado');
        if (created && created.id) navigate('/clientes/' + created.id, { replace: true });
      } else {
        const updated = await svc.updateCliente(id, payload);
        if (updated) setForm((f) => ({ ...f, ...updated }));
        pushMsg('ok', 'Cliente actualizado');
      }
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }, [isNew, id, navigate, buildPayload, pushMsg]);

  const onToggleActivo = useCallback(async () => {
    if (isNew || !id) return;
    setActing(true);
    try {
      if (form.is_active) {
        await svc.deactivateCliente(id);
        setForm((f) => ({ ...f, is_active: false }));
        pushMsg('ok', 'Cliente inactivado');
      } else {
        await svc.activateCliente(id);
        setForm((f) => ({ ...f, is_active: true }));
        pushMsg('ok', 'Cliente activado');
      }
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error');
    } finally {
      setActing(false);
    }
  }, [isNew, id, form.is_active, pushMsg]);

  return {
    id, isNew, navigate,
    form, set,
    tab, setTab,
    loaded, saving, acting, msg,
    onSave, onToggleActivo,
    clearMsg: () => setMsg(null),
  };
}
